import { ResolvableModeEnum } from '@e-socna/data-layer';
import { ensureDefined, invariant, isDefined } from '@e-socna/utils';
import type { ResolverEnum, ResolverId, Id } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';

interface Params<V> {
  readonly mode: ResolvableModeEnum;
  readonly resolverId?: Id | null | undefined;
  readonly value?: V | null | undefined;
}

interface DynamicResult {
  readonly mode: ResolvableModeEnum.Dynamic;
  readonly resolverCode: NonNullable<ResolverEnum>;
  readonly value: undefined;
  readonly merged: NonNullable<ResolverEnum>;
}

interface StaticResult<V> {
  readonly mode: ResolvableModeEnum.Static;
  readonly resolverCode: undefined;
  readonly value: V | null | undefined;
  readonly merged: V | null | undefined;
}

type ResolvableResult<V, M> = M extends ResolvableModeEnum.Dynamic
  ? DynamicResult
  : M extends ResolvableModeEnum.Static
    ? StaticResult<V>
    : never;

export const useResolvableNormalizer = <V, M>({
  mode,
  resolverId,
  value,
}: Params<V> & { mode: M }): ResolvableResult<V, M> => {
  const { resolversIdMap } = useReferencesContext();

  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  if (mode === ResolvableModeEnum.Dynamic) {
    invariant(
      isDefined(resolverId),
      'Dynamic resolvable must have a resolverId',
    );

    const { resolverCode } = ensureDefined(
      { resolverCode: resolversIdMap[resolverId as ResolverId]?.code },
      `Unknown resolverId: ${resolverId}`,
    );

    return {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
      mode: mode as ResolvableModeEnum.Dynamic,
      resolverCode,
      value: undefined,
      merged: resolverCode,
    } as ResolvableResult<V, M>;
  }

  invariant(
    !isDefined(resolverId),
    'Static resolvable must not have a resolverId',
  );

  return {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    mode: mode as ResolvableModeEnum.Static,
    resolverCode: undefined,
    value,
    merged: value,
  } as ResolvableResult<V, M>;
};
